import { Link } from '@tanstack/react-router'

import YearRangeTrack from '@/components/charts/YearRangeTrack'
import CardState, { type CardStateValue } from '@/components/ui/CardState'
import Money from '@/components/ui/Money'
import Skeleton from '@/components/ui/Skeleton'
import Tooltip from '@/components/ui/Tooltip'
import { yearRangeScore } from '@/features/market/lib/yearRange'
import { useYearRange } from '@/features/market-rail/api/queries'
import type { YearRange } from '@/features/market-rail/api/railSchemas'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { toBsDate } from '@/lib/bsDate'
import { toChartNumber } from '@/lib/money'

/** "14 Poush": the day a bound was set, without the year. */
const bsDay = (iso: string | null): string | null => toBsDate(iso)?.format('D MMMM', 'en') ?? null

/**
 * The mock's tip over the bar: "29% of the way from the low (14 Poush) to the high (3 Baisakh)".
 * A bound whose day the year's history does not show is named without one.
 */
const yearRangeTip = (percent: number, lowOn: string | null, highOn: string | null) => {
  const low = bsDay(lowOn)
  const high = bsDay(highOn)
  return `${String(percent)}% of the way from the low${low === null ? '' : ` (${low})`} to the high${high === null ? '' : ` (${high})`}`
}

function RangeSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="mt-2 h-2 w-full rounded-xs" />
      <div className="flex justify-between">
        <Skeleton className="h-4 w-14 rounded-xs" />
        <Skeleton className="h-4 w-14 rounded-xs" />
      </div>
    </div>
  )
}

/** The track, the marker at today's level and the two ends under it. */
function RangeBar({ range }: { range: YearRange }) {
  const score =
    yearRangeScore(toChartNumber(range.now), toChartNumber(range.low), toChartNumber(range.high)) ??
    0
  const percent = Math.round(score)

  return (
    <div className="flex flex-col gap-2">
      <Tooltip
        title={yearRangeTip(percent, range.lowOn, range.highOn)}
        variant="chip"
        align="center"
      >
        <YearRangeTrack
          score={score}
          markerClassName="ring-background outline-offset-3 outline-foreground/25 transition-[outline-width] duration-fast group-hover/range:outline-2 group-focus-visible/range:outline-2"
        />
      </Tooltip>
      <span className="flex justify-between text-body-xs text-muted-foreground">
        <Money value={range.low} />
        <Money value={range.high} />
      </span>
    </div>
  )
}

/**
 * "52-week range" on the Market rail (the mock's `mrail__range`): NEPSE today, and where that
 * sits between the year's low and high, the ends printed under the track. The whole block is one
 * link to the 52-week page: on hover it washes 8px past every side, a "›" slides in after the
 * title and the marker takes a second ring; the bar's tip says how far up the range today is and
 * the days the two ends were set.
 *
 * The level and the ends are `/market/summary/`'s `week52`; the days are read off the year's
 * history (`railService.yearRange`). No year range, no block.
 */
export default function YearRangeCard() {
  const block = useRailBlock('range52')
  const { data, isPending, isError } = useYearRange()

  const state: CardStateValue = isPending
    ? 'loading'
    : isError
      ? 'failed'
      : data === null
        ? 'empty'
        : 'loaded'

  return (
    <section aria-label="52-week range" {...block}>
      <Link
        to="/market/52-week"
        aria-label="52-week range: open the 52-week page"
        className="group/range block rail-block-wash outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <span className="flex items-center justify-between gap-3 pb-2">
          <span className="text-h-s text-foreground">
            52-week range{' '}
            <span
              aria-hidden="true"
              className="inline-block -translate-x-1 text-muted-foreground-subtle opacity-0 transition-[opacity,translate] duration-med ease-out group-hover/range:translate-x-0 group-hover/range:opacity-100 group-focus-visible/range:translate-x-0 group-focus-visible/range:opacity-100"
            >
              ›
            </span>
          </span>
          {data ? <Money value={data.now} className="text-h-s text-foreground" /> : null}
        </span>
        <CardState
          state={state}
          size="compact"
          skeleton={<RangeSkeleton />}
          failedTitle="Couldn't load the range"
          failedDescription="Try again shortly."
          emptyTitle="No range yet"
          emptyDescription="It appears once the year's low and high are known."
        >
          {data ? <RangeBar range={data} /> : null}
        </CardState>
      </Link>
    </section>
  )
}
